import { ArrowDownToLine, ArrowUpFromLine, CalendarClock, Loader2, TrendingUp } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { fetchAdminExternalPnL } from '@/api/admin';
import { fmtUsdAmount as fmtUsd } from '@/lib/money';

/**
 * ExternalPnLCard — calendar cash-flow widget for the admin Dashboard.
 *
 * Data source: GET /api/admin/pnl/external (admin-gated, read-only).
 * Polling cadence: 10 seconds (matches existing admin dashboard rhythm —
 * stays within the global Layer-7 sliding-window rate limit).
 *
 * Buckets shown (tab strip): Today · Week (Sun→Sat) · Month · Year · All-Time.
 * All windows are computed server-side in America/New_York; the frontend
 * renders human-friendly EST date labels for the active period.
 */
const COLOR_GOLD = '#FFC20E';
const COLOR_PROFIT = '#10B981';
const COLOR_LOSS = '#EF4444';

const BUCKETS = [
  { id: 'today', label: 'Today' },
  { id: 'week', label: 'This Week' },
  { id: 'month', label: 'This Month' },
  { id: 'year', label: 'This Year' },
  { id: 'all_time', label: 'All-Time' },
];

const fmtEstDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    // Format using the offset that is already baked into the ISO string
    // (e.g., -04:00 EDT). We render MM/DD/YYYY for compactness.
    const month = String(d.getUTCMonth() + 1).padStart(2, '0');
    const day = String(d.getUTCDate()).padStart(2, '0');
    const year = d.getUTCFullYear();
    return `${month}/${day}/${year}`;
  } catch {
    return '—';
  }
};

const ExternalPnLCard = () => {
  const [data, setData] = useState(null);
  const [active, setActive] = useState('today');
  const [errored, setErrored] = useState(false);
  const [loading, setLoading] = useState(true);

  const probe = useCallback(async () => {
    try {
      const r = await fetchAdminExternalPnL();
      setData(r);
      setErrored(false);
    } catch {
      setErrored(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    probe();
    const t = setInterval(probe, 60000);
    return () => clearInterval(t);
  }, [probe]);

  const bucket = data?.buckets?.[active] || null;
  const netRaw = bucket ? parseFloat(bucket.net) : 0;
  const netColor = netRaw > 0 ? COLOR_PROFIT : netRaw < 0 ? COLOR_LOSS : COLOR_GOLD;
  const netSign = netRaw > 0 ? '+' : netRaw < 0 ? '−' : '';
  const netAbs = Math.abs(netRaw);

  return (
    <section
      className="sc-glass-strong rounded-2xl p-5 md:p-7 relative overflow-hidden"
      style={{
        border: '1px solid rgba(255, 182, 18, 0.32)',
      }}
      data-testid="admin-external-pnl-card"
    >
      <div
        className="absolute -top-16 -right-16 w-56 h-56 pointer-events-none rounded-full"
        style={{
          background: 'radial-gradient(circle, rgba(255,182,18,0.18), transparent 60%)',
        }}
        aria-hidden="true"
      />
      {/* Header */}
      <div className="relative z-10 flex items-start justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center border shrink-0"
            style={{
              background: 'rgba(255,182,18,0.06)',
              borderColor: 'rgba(255,182,18,0.32)',
            }}
            aria-hidden="true"
          >
            <CalendarClock className="w-5 h-5 text-[#FFC20E]" />
          </div>
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">External Cash Flow · EST</div>
            <div className="font-display font-bold text-base md:text-lg mt-0.5">Deposits In vs Withdrawal Payouts Out</div>
          </div>
        </div>
        <span
          className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] px-2.5 py-1 rounded-full border shrink-0"
          style={{
            color: '#FFE08A',
            background: 'rgba(255,182,18,0.10)',
            borderColor: 'rgba(255,182,18,0.32)',
          }}
        >
          <span className="inline-block w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: COLOR_GOLD }} aria-hidden="true" />
          Live
        </span>
      </div>

      {/* Tab pills */}
      <div className="relative z-10 flex items-center gap-2 flex-wrap mb-5" role="tablist" aria-label="Time period">
        {BUCKETS.map((b) => {
          const selected = active === b.id;
          return (
            <button
              key={b.id}
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => setActive(b.id)}
              className="px-3 py-1.5 rounded-full text-[11px] uppercase tracking-[0.18em] border transition"
              style={{
                color: selected ? '#0a0a0b' : 'var(--sc-text-mute)',
                background: selected ? 'linear-gradient(135deg, #FFC20E, #FFB612)' : 'rgba(0,0,0,0.32)',
                borderColor: selected ? 'rgba(255,182,18,0.85)' : 'rgba(255,182,18,0.18)',
                fontWeight: selected ? 700 : 500,
              }}
              data-testid={`pnl-bucket-${b.id}`}
            >
              {b.label}
            </button>
          );
        })}
      </div>

      {/* Body */}
      {loading && !data ? (
        <div className="relative z-10 flex items-center gap-2 text-[12px] text-[var(--sc-text-mute)] py-6" data-testid="external-pnl-loading">
          <Loader2 className="w-4 h-4 animate-spin text-[#FFC20E]" />
          Loading cash-flow snapshot…
        </div>
      ) : errored && !data ? (
        <div className="relative z-10 text-[12px] text-[var(--sc-text-mute)] py-6" data-testid="external-pnl-error">
          Snapshot temporarily unavailable. Reconnecting…
        </div>
      ) : bucket ? (
        <div className="relative z-10">
          {/* Net hero */}
          <div className="text-center mb-5">
            <div className="text-[10px] uppercase tracking-[0.30em] text-[var(--sc-text-mute)]">Net (Deposits − Withdrawals)</div>
            <div
              className="sc-tabular font-display font-extrabold mt-2 leading-none"
              style={{
                color: netColor,
                fontSize: 'clamp(28px, 5vw, 42px)',
                textShadow: `0 0 18px ${netColor}33`,
              }}
              data-testid={`pnl-net-${active}`}
            >
              {netSign}${fmtUsd(netAbs)}
            </div>
          </div>

          {/* Twin stat tiles */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
            <div
              className="rounded-xl p-3 md:p-4 border"
              style={{
                background: 'rgba(0,0,0,0.32)',
                borderColor: 'rgba(16, 185, 129, 0.28)',
              }}
              data-testid="pnl-deposits-in-tile"
            >
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.20em] text-[var(--sc-text-mute)]">
                <ArrowDownToLine className="w-3.5 h-3.5" style={{ color: COLOR_PROFIT }} />
                Deposits In
              </div>
              <div
                className="sc-tabular font-display font-bold text-2xl md:text-3xl mt-1.5 leading-none"
                style={{ color: COLOR_PROFIT }}
                data-testid={`pnl-deposits-${active}`}
              >
                +${fmtUsd(bucket.deposits_in)}
              </div>
            </div>
            <div
              className="rounded-xl p-3 md:p-4 border"
              style={{
                background: 'rgba(0,0,0,0.32)',
                borderColor: 'rgba(239, 68, 68, 0.24)',
              }}
              data-testid="pnl-withdrawals-out-tile"
            >
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.20em] text-[var(--sc-text-mute)]">
                <ArrowUpFromLine className="w-3.5 h-3.5" style={{ color: COLOR_LOSS }} />
                Withdrawals Out
              </div>
              <div
                className="sc-tabular font-display font-bold text-2xl md:text-3xl mt-1.5 leading-none"
                style={{ color: COLOR_LOSS }}
                data-testid={`pnl-withdrawals-${active}`}
              >
                −${fmtUsd(bucket.withdrawals_out)}
              </div>
            </div>
          </div>

          {/* Period footer */}
          <div
            className="mt-4 text-[10px] uppercase tracking-[0.20em] text-[var(--sc-text-mute)] flex items-center justify-between flex-wrap gap-2"
            data-testid="pnl-period-footer"
          >
            <span className="flex items-center gap-2">
              <TrendingUp className="w-3 h-3 text-[#FFC20E]" aria-hidden="true" />
              {fmtEstDate(bucket.period_start_est)} — {fmtEstDate(bucket.period_end_est)} (EST)
            </span>
            <span className="text-[var(--sc-text-mute)]">Resets at 12:00 AM EST · Week: Sun → Sat</span>
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default ExternalPnLCard;
